/* ==========================================================================
   Aklasu — collection description expander
   --------------------------------------------------------------------------
   Collapses a long collection description to a few lines with a Read more
   toggle, WITHOUT hiding it from search engines.

   THE SEO RULE THIS IS BUILT AROUND
   The full description stays in the DOM at all times and is never
   `display: none`, never `visibility: hidden`, never moved off-screen. The
   collapsed state is purely a `max-height` clip in CSS. Google renders the
   page and indexes clipped-but-rendered text at full weight; it is content
   inside accordions, tabs and `display: none` that carries any risk. That
   is also why this is not a <details> element, which would be the tidier
   markup but puts the text behind `display: none` when closed.

   The grenadine description carries four internal product links. Those have
   to stay crawlable AND clickable, which rules out anything that strips or
   defers the markup.

   PROGRESSIVE ENHANCEMENT
   The clamp is applied by this script, not by the stylesheet. With JS
   disabled or broken the description simply renders in full, which is the
   correct failure mode: worse layout, nothing lost. The toggle button is
   hidden by default and only revealed once we have confirmed the text
   actually overflows.

   NO HEIGHT ANIMATION, deliberately. Animating max-height between a fixed
   value and a large arbitrary one produces a snap followed by a drift,
   because the easing runs over a distance the content never occupies. An
   instant toggle under a soft mask edge is calmer than a janky one.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = '[data-ak-desc]';
  var OVERFLOW_TOLERANCE = 4; // px, same slop as ak-reviews.js

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wraps = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < wraps.length; i++) setup(wraps[i]);
  }

  function setup(wrap) {
    if (wrap.getAttribute('data-ak-desc-ready') === '1') return;

    var rte = wrap.querySelector('.rte');
    var btn = wrap.querySelector('[data-ak-desc-toggle]');
    if (!rte || !btn) return;

    wrap.setAttribute('data-ak-desc-ready', '1');

    measure(wrap, rte, btn);

    // A webfont swapping in after first paint changes the line count, which
    // can flip a description from "overflows" to "fits" or back. Re-measure
    // once fonts settle rather than trusting the fallback-font layout.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (btn.getAttribute('aria-expanded') === 'true') return; // user acted, leave alone
        wrap.classList.remove('is-toggleable');
        measure(wrap, rte, btn);
      })['catch'](function () {});
    }

    // The paragraph boundary is a pixel value measured at one width, so it
    // goes stale the moment the text reflows: a rotated phone, a resized
    // desktop window. Re-measure, but only while collapsed. Recomputing
    // under someone who has deliberately expanded the text would yank the
    // page out from under them.
    var t;
    window.addEventListener('resize', function () {
      if (btn.getAttribute('aria-expanded') === 'true') return;
      clearTimeout(t);
      t = setTimeout(function () {
        wrap.classList.remove('is-toggleable');
        measure(wrap, rte, btn);
      }, 150);
    });
  }

  function lineHeightOf(el) {
    var cs = window.getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    // `line-height: normal` parses to NaN. Approximate rather than divide
    // by nothing and clamp the description to zero.
    if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.5;
    return lh;
  }

  function measure(wrap, rte, btn) {
    wrap.classList.remove('is-clamped', 'has-soft-edge');
    wrap.style.removeProperty('--ak-desc-clamp');

    var cap = maxLines(wrap) * lineHeightOf(rte);

    // Whole thing already fits. No clip, no button, no noise.
    if (rte.scrollHeight <= cap + OVERFLOW_TOLERANCE) return;

    // PREFER A PARAGRAPH BOUNDARY OVER A LINE COUNT.
    // Cutting at a line count leaves a half-sentence dissolving under the
    // mask, which is the visual language of engagement bait. The copy on
    // these pages opens with a proper lede, so we clip to the last
    // paragraph that fits inside the cap and let it land whole. Only when
    // the very first paragraph is longer than the cap on its own do we
    // fall back to a line clip, and that is the one case where the soft
    // mask edge earns its place.
    var top = rte.getBoundingClientRect().top;
    var kids = rte.children;
    var boundary = 0;

    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      var bottom = el.getBoundingClientRect().bottom - top;
      if (bottom > cap) break;

      /* NEVER END ON A HEADING.
         These descriptions used to be plain paragraphs; they now
         carry H2 subheads for SEO. Accepting any child as a
         boundary meant the clamp could land on the bottom of a
         heading, leaving a subhead introducing a section the
         reader cannot see, directly above "Read more". That looks
         like broken content, which is worse than a line clip.

         Measured on /collections/mens-dress-socks at 530px: the
         children are p(58) h2(98) p(287), the cap was 128, so the
         boundary resolved to 98 — the bottom of the heading.
         Skipping headings resolves it to 58 instead, the end of
         the opening paragraph, which is what this was always
         meant to do. */
      if (/^H[1-6]$/.test(el.tagName)) continue;

      boundary = bottom;
    }

    if (boundary > 0) {
      wrap.style.setProperty('--ak-desc-clamp', Math.round(boundary) + 'px');
    } else {
      wrap.style.setProperty('--ak-desc-clamp', Math.round(cap) + 'px');
      wrap.classList.add('has-soft-edge');
    }

    wrap.classList.add('is-clamped', 'is-toggleable');
    setState(wrap, btn, false);
    bind(wrap, rte, btn);
  }

  function maxLines(wrap) {
    var n = parseInt(wrap.getAttribute('data-ak-desc-lines'), 10);
    return n > 0 ? n : 5;
  }

  function bind(wrap, rte, btn) {
    if (btn.getAttribute('data-ak-desc-bound') === '1') return;
    btn.setAttribute('data-ak-desc-bound', '1');

    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';

      // Collapsing while focus sits on a product link inside the clipped
      // region would leave the focus ring somewhere the user cannot see.
      // Bring it back to the control they just pressed.
      if (expanded && rte.contains(document.activeElement)) btn.focus();

      setState(wrap, btn, !expanded);
    });

    // Keyboard users tab through the product links in paragraph two before
    // they ever reach the toggle. Without this they would be chasing an
    // invisible focus ring through clipped text. Opening on focus is the
    // established fix and costs a sighted mouse user nothing.
    rte.addEventListener('focusin', function () {
      if (btn.getAttribute('aria-expanded') !== 'true') setState(wrap, btn, true);
    });
  }

  function setState(wrap, btn, expanded) {
    if (expanded) wrap.classList.remove('is-clamped');
    else wrap.classList.add('is-clamped');

    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    var label = expanded ? btn.getAttribute('data-less') : btn.getAttribute('data-more');
    if (label) btn.textContent = label;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders the section on every settings change.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
